import React, { useEffect, useState, useRef } from "react";
import useApi from '../api';
import { useSearchParams } from 'react-router-dom';
// import SpinnerLocal from "../ui/SpinnerLocal";
import { toast } from "react-toastify";
import HubSpotIcon from '../icons/hubspot_logo.webp';
// import OntraportIcon from '../icons/ontraport_logo.webp'
// import GoogleCalendarIcon from '../icons/google_calendar.webp';
// import Ontraport from './Ontraport';
import ChiliPiper from './ChiliPiper';
import DefaultCom from './DefaultCom';
import RevenueHero from './RevenueHero';
import Calendly from './Calendly';
import TidyCal from './TidyCal';
import CalCom from './CalCom';
import HubSpotMeetings from './HubSpotMeetings';
import ChiliPiperIcon from '../icons/chili_piper_logo.png';
import CalComIcon from '../icons/cal_logo.webp';
import DefaultIcon from '../icons/default_logo.jpeg';
import RevenueHeroIcon from '../icons/revenue_hero_logo.jpeg';
import SalesforceIcon from '../icons/salesforce_logo.png';
import AttioIcon from '../icons/attio_logo.png';
import CalendlyIcon from '../icons/calendly_logo.png';
import TidyCalIcon from '../icons/tidycal_logo.jpeg';
import SlackIcon from '../icons/slack_logo.webp';
import GoogleAdsIcon from '../icons/google_ads_logo.png';
import Webhooks from './Webhooks';
import APIKeys from './APIKeys';
import IdentityVerification from './IdentityVerification';
import McpServers from './McpServers';
import StripePayments from './StripePayments';

import { useUser } from "../user/UserContext";
import { Separator } from "@/components/ui/separator";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2, Plus, RefreshCw, Trash2, TriangleAlert } from "lucide-react"
import { calendarLinkLabel } from "../../lib/calendarIntegrationLabel";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs" 

interface Integration {
    id: string;
    name: string;
    description: string;
    details: any;
}

interface IntegrationParams {
    appId?: string;
    apiKey?: string;
    domainName?: string;
    inboundRouter?: string;
    calendlyUrl?: string;
    tidyCalUrl?: string;
    calComUrl?: string;
    hubspotMeetingsUrl?: string;
    hubspotMeetingsLinkName?: string;
    formId?: string;
    teamId?: string;
    routerId?: string;
    appName?: string;
}

const MULTI_LINK_CALENDARS = ['hubspot-meetings', 'tidycal', 'cal-com'];

const typeMap = {
    'google-calendar': 'user',
    'chili-piper': 'organization',
    'hubspot': 'organization',
    'salesforce': 'organization',
    'attio': 'organization',
    'ontraport': 'organization',
    'calendly': 'user',
    'tidycal': 'user',
    'cal-com': 'user',
    'hubspot-meetings': 'user',
    'slack': 'organization',
    'google-ads': 'organization',
    'default': 'organization',
    'revenue-hero': 'organization'
}

const RETURN_TO_STORAGE_KEY = 'integrations:return-to';

interface IntegrationConfig {
    name: string;
    sysName: string;
    description: string;
    icon: string;
    connectHandler: () => Promise<void>;
    minPlan?: 'pro' | 'business';
    reconnectLabel?: string;
}

const Integrations: React.FC = () => {
    const { get, post, remove } = useApi();
    const [searchParams, setSearchParams] = useSearchParams();
    const [integrations, setIntegrations] = useState<Integration[]>([]);
    const [fetching, setFetching] = useState<boolean>(false);
    const [creatingIntegration, setCreatingIntegration] = useState<boolean>(false);
    const [chiliPiperModalVisible, setChiliPiperModalVisible] = useState<boolean>(false);
    const [calendlyModalVisible, setCalendlyModalVisible] = useState<boolean>(false);
    const [tidyCalModalVisible, setTidyCalModalVisible] = useState<boolean>(false);
    const [calComModalVisible, setCalComModalVisible] = useState<boolean>(false);
    const [hubspotMeetingsModalVisible, setHubspotMeetingsModalVisible] = useState<boolean>(false);
    const [defaultModalVisible, setDefaultModalVisible] = useState<boolean>(false);
    const [revenueHeroModalVisible, setRevenueHeroModalVisible] = useState<boolean>(false);
    const [checkingLicense, setCheckingLicense] = useState<boolean>(true);
    const { user, organization, checkLicenseType } = useUser();
    // Services Phase 7 rollout flag (SERVICES_COMMERCE_ADR.md A6): the tab
    // exists only for orgs Aimdoc has enabled for commerce.
    const [commerceEnabled, setCommerceEnabled] = useState<boolean>(false);
    useEffect(() => {
        get('/services/commerce/stripe/account')
            .then((response) => setCommerceEnabled(!!response.data?.enabled))
            .catch(() => setCommerceEnabled(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const name = searchParams.get('name');
    const connect = searchParams.get('connect');
    const returnTo = searchParams.get('returnTo');
    
    // Get the active tab from URL params, default to 'integrations'
    const activeTab = searchParams.get('tab') || 'integrations';
    
    // Handler to update URL when tab changes
    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
    };

    const creatingIntegrationRef = useRef(creatingIntegration);
    creatingIntegrationRef.current = creatingIntegration;
    const autoConnectRef = useRef<string | null>(null);

    const connectGoogle = async () => {
        const response = await post('/google-drive-url/', {});
        const authUrl = response.data.auth_url;
        window.open(authUrl, '_blank');
    };

    const connectGoogleCalendar = async () => {
        const response = await post('/google-calendar-url/', {});
        const authUrl = response.data.auth_url;
        window.open(authUrl, '_blank');
    };

    const connectHubSpot = async () => {
        const response = await post('/hubspot-url/', {});
        const authUrl = response.data.auth_url;
        window.open(authUrl, '_blank');
    };

    const connectSalesforce = async () => {
        const response = await post('/salesforce-url/', {});
        const authUrl = response.data.auth_url;
        window.open(authUrl, '_blank');
    };

    const connectAttio = async () => {
        const response = await post('/attio-url/', {});
        const authUrl = response.data.auth_url;
        window.open(authUrl, '_blank');
    };

    const connectChiliPiper = async () => {
        setChiliPiperModalVisible(true);
    };

    const connectDefault = async () => {
        setDefaultModalVisible(true);
    };

    const connectRevenueHero = async () => {
        setRevenueHeroModalVisible(true);
    };

    const connectCalendly = async () => {
        const response = await post('/calendly-url/', {});
        const authUrl = response.data.auth_url;
        window.location.href = authUrl; // Full redirect for Calendly OAuth
    };

    const connectTidyCal = async () => {
        setTidyCalModalVisible(true);
    };

    const connectCalCom = async () => {
        setCalComModalVisible(true);
    };

    const connectHubSpotMeetings = async () => {
        setHubspotMeetingsModalVisible(true);
    };

    const connectSlack = async (sameTab = false) => {
        const response = await post('/slack-url/', {});
        const authUrl = response.data.auth_url;
        if (sameTab) {
            window.location.href = authUrl;
            return;
        }
        window.open(authUrl, '_blank');
    };

    const connectGoogleAds = async () => {
        const response = await post('/google-ads-url/', {});
        const authUrl = response.data.auth_url;
        window.open(authUrl, '_blank');
    };

    const storeReturnTo = (target: string) => {
        try {
            localStorage.setItem(RETURN_TO_STORAGE_KEY, target);
        } catch (error) {
            console.warn('Failed to store return target for integrations redirect', error);
        }
    };

    const clearStoredReturnTo = () => {
        try {
            localStorage.removeItem(RETURN_TO_STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to clear integrations return target', error);
        }
    };

    const getSafeReturnTo = (): string | null => {
        const candidate = returnTo || localStorage.getItem(RETURN_TO_STORAGE_KEY);
        if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) {
            return null;
        }

        try {
            const parsed = new URL(candidate, window.location.origin);
            if (parsed.origin !== window.location.origin) {
                return null;
            }
            return `${parsed.pathname}${parsed.search}${parsed.hash}`;
        } catch (error) {
            console.warn('Invalid return target for integrations redirect', error);
            return null;
        }
    };

    const redirectAfterIntegrationSuccess = () => {
        const redirectTarget = getSafeReturnTo();
        clearStoredReturnTo();
        window.location.href = redirectTarget || '/integrations';
    };

    const triggerConnectFlow = async (integrationName: string) => {
        switch (integrationName) {
            case 'google':
                await connectGoogle();
                return true;
            case 'google-calendar':
                await connectGoogleCalendar();
                return true;
            case 'hubspot':
                await connectHubSpot();
                return true;
            case 'salesforce':
                await connectSalesforce();
                return true;
            case 'attio':
                await connectAttio();
                return true;
            case 'chili-piper':
                await connectChiliPiper();
                return true;
            case 'default':
                await connectDefault();
                return true;
            case 'revenue-hero':
                await connectRevenueHero();
                return true;
            case 'calendly':
                await connectCalendly();
                return true;
            case 'tidycal':
                await connectTidyCal();
                return true;
            case 'cal-com':
                await connectCalCom();
                return true;
            case 'hubspot-meetings':
                await connectHubSpotMeetings();
                return true;
            case 'slack':
                await connectSlack(true);
                return true;
            case 'google-ads':
                await connectGoogleAds();
                return true;
            default:
                return false;
        }
    };

    const createIntegration = async (integration: IntegrationParams) => {
        if (creatingIntegrationRef.current) return;

        const integrationType = integration.appName || name;
        if (!integrationType || !(integrationType in typeMap)) {
            toast.error('Invalid integration type');
            return;
        }

        setCreatingIntegration(true);
        try {
            await post('/integrations/', {
                code,
                state,
                name: integrationType,
                type: typeMap[integrationType as keyof typeof typeMap],
                app_id: integration.appId,
                api_key: integration.apiKey,
                domain_name: integration.domainName,
                inbound_router: integration.inboundRouter,
                calendly_url: integration.calendlyUrl,
                tidy_cal_url: integration.tidyCalUrl,
                cal_com_url: integration.calComUrl,
                hubspot_meetings_url: integration.hubspotMeetingsUrl,
                hubspot_meetings_link_name: integration.hubspotMeetingsLinkName,
                form_id: integration.formId,
                team_id: integration.teamId,
                router_id: integration.routerId
            });
            
            // Special handling for Calendly - show event type selection modal
            if (integrationType === 'calendly' && code) {
                await getIntegrations(); // Refresh integrations list
                setCalendlyModalVisible(true); // Show modal for event type selection
            } else {
                if (getSafeReturnTo()) {
                    redirectAfterIntegrationSuccess();
                    return;
                }
                if (code) {
                    window.location.href = '/integrations';
                    return;
                }
                toast.success('Integration created successfully', { autoClose: 2000 });
                await getIntegrations();
            }
        } catch (error) {
            toast.error('Failed to create integration');
        } finally {
            setCreatingIntegration(false);
            if (integrationType !== 'calendly') {
                setChiliPiperModalVisible(false);
                setDefaultModalVisible(false);
                setRevenueHeroModalVisible(false);
                setCalendlyModalVisible(false);
                setTidyCalModalVisible(false);
                setCalComModalVisible(false);
                setHubspotMeetingsModalVisible(false);
            }
        }
    };

    const deleteIntegration = async (id: string) => {
        try {
            await remove(`/integrations/${id}`);
            toast.success('Integration disconnected successfully', { autoClose: 2000 });
            setIntegrations(integrations.filter(integration => integration.id !== id));
        } catch (error: any) {
            const errorDetail = error?.detail;
            let errorMessage = 'Failed to delete integration';

            if (errorDetail) {
                errorMessage = typeof errorDetail === 'object' ? errorDetail.message : errorDetail;

                if (errorDetail.agents && Array.isArray(errorDetail.agents)) {
                    errorMessage += `\nAffected agents: ${errorDetail.agents.join(', ')}`;
                }
            }

            toast.error(errorMessage, { autoClose: 5000 });
        }
    };

    const getIntegrations = async () => {
        setFetching(true);
        try {
            const response = await get('/integrations/');
            setIntegrations(response.data);
        } catch (error) {
            toast.error('Failed to fetch integrations', { autoClose: 2000 });
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        getIntegrations();
    }, []);

    useEffect(() => {
        if (returnTo) {
            storeReturnTo(returnTo);
        }
    }, [returnTo]);

    useEffect(() => {
        if (!connect && !returnTo && !name && !code) {
            clearStoredReturnTo();
        }
    }, [connect, returnTo, name, code]);

    useEffect(() => {
        if (!connect || (name && code)) {
            return;
        }

        if (autoConnectRef.current === connect) {
            return;
        }

        const isFreeTierIntegration = connect === 'calendly' || connect === 'tidycal' || connect === 'cal-com' || connect === 'hubspot-meetings';
        const requiredPlan = isFreeTierIntegration ? null : 'pro';
        const hasAccess = requiredPlan ? checkLicenseType(requiredPlan) : true;
        if (!hasAccess) {
            autoConnectRef.current = connect;
            return;
        }

        autoConnectRef.current = connect;
        triggerConnectFlow(connect)
            .then((started) => {
                if (!started) {
                    autoConnectRef.current = null;
                }
            })
            .catch(() => {
                autoConnectRef.current = null;
            });
    }, [connect, name, code, checkLicenseType]);

    useEffect(() => {
        if (name && code && !creatingIntegrationRef.current) {
            // All OAuth integrations proceed with code
            createIntegration({
                appId: undefined,
                apiKey: undefined,
                domainName: undefined,
                inboundRouter: undefined,
                appName: name
            });
        }
    }, [name]);

    useEffect(() => {
        if (user && organization) {
            setCheckingLicense(false);
        }
    }, [user, organization]);

    const SkeletonIntegrationCard: React.FC = () => {
        return (
            <Card className="flex min-h-[218px] flex-col">
                <CardHeader className="p-5 pb-3">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 animate-pulse rounded-lg bg-muted"></div>
                        <div className="h-4 w-28 animate-pulse rounded bg-muted"></div>
                    </div>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1">
                    <div className="space-y-2">
                        <div className="h-3 w-full animate-pulse rounded bg-muted"></div>
                        <div className="h-3 w-4/5 animate-pulse rounded bg-muted"></div>
                    </div>
                </CardContent>
                <CardFooter className="mt-auto p-5 pt-0">
                    <div className="h-9 w-full animate-pulse rounded-md bg-muted"></div>
                </CardFooter>
            </Card>
        );
    };

    const integrationConfigs: IntegrationConfig[] = [
        {
            name: 'Slack',
            sysName: 'slack',
            description: 'Send notifications about visitor interactions and contacts to your Slack channels.',
            icon: SlackIcon,
            connectHandler: connectSlack
        },
        {
            name: 'Google Ads',
            sysName: 'google-ads',
            description: 'Track conversions and optimize your Google Ads campaigns with Aimdoc visitor data.',
            icon: GoogleAdsIcon,
            connectHandler: connectGoogleAds
        },
        {
            name: 'HubSpot',
            sysName: 'hubspot',
            description: 'Creates an Aimdoc form in your HubSpot instance and submits contact data when a contact is created in Aimdoc.',
            icon: HubSpotIcon,
            connectHandler: connectHubSpot
        },
        {
            name: 'Salesforce',
            sysName: 'salesforce',
            description: 'Creates a contact in Salesforce when a contact is created in Aimdoc.',
            icon: SalesforceIcon,
            connectHandler: connectSalesforce
        },
        {
            name: 'Attio',
            sysName: 'attio',
            description: 'Creates or updates people in Attio and adds Aimdoc session notes when contacts are created in Aimdoc.',
            icon: AttioIcon,
            connectHandler: connectAttio
        },
        {
            name: 'Chili Piper',
            sysName: 'chili-piper',
            description: 'Schedule meetings with customers via Chili Piper.',
            icon: ChiliPiperIcon,
            connectHandler: connectChiliPiper
        },
        {
            name: 'Default',
            sysName: 'default',
            description: 'Schedule meetings with customers via Default.',
            icon: DefaultIcon,
            connectHandler: connectDefault
        },
        {
            name: 'RevenueHero',
            sysName: 'revenue-hero',
            description: 'Schedule meetings with customers via RevenueHero.',
            icon: RevenueHeroIcon,
            connectHandler: connectRevenueHero
        },
        {
            name: 'Calendly',
            sysName: 'calendly',
            description: 'Schedule meetings with customers via Calendly.',
            icon: CalendlyIcon,
            connectHandler: connectCalendly
        },
        {
            name: 'TidyCal',
            sysName: 'tidycal',
            description: 'Schedule meetings with customers via TidyCal.',
            icon: TidyCalIcon,
            connectHandler: connectTidyCal
        },
        {
            name: 'Cal.com',
            sysName: 'cal-com',
            description: 'Schedule meetings with customers via Cal.com.',
            icon: CalComIcon,
            connectHandler: connectCalCom
        },
        {
            name: 'HubSpot Meetings',
            sysName: 'hubspot-meetings',
            description: 'Schedule meetings with customers via HubSpot Meetings.',
            icon: HubSpotIcon,
            connectHandler: connectHubSpotMeetings
        }
    ];

    const renderIntegrationCard = (config: IntegrationConfig) => {
        const matchingIntegrations = integrations.filter(integration => integration.name === config.sysName);
        const existingIntegration = matchingIntegrations[0];
        const isMultiLinkCalendar = MULTI_LINK_CALENDARS.includes(config.sysName);
        const isFreeTierIntegration = config.sysName === 'calendly' || config.sysName === 'tidycal' || config.sysName === 'cal-com' || config.sysName === 'hubspot-meetings';
        const requiredPlan = isFreeTierIntegration ? null : (config.minPlan || 'pro');
        const hasAccess = requiredPlan ? checkLicenseType(requiredPlan) : true;
        
        // Detect legacy Calendly integration (has calendly_url but no access_token)
        const isLegacyCalendly = config.sysName === 'calendly' &&
            existingIntegration?.details?.calendly_url &&
            !existingIntegration?.details?.access_token;

        // Slack installs that predate the live chat scopes must reconnect to
        // grant them before Live Chat Takeover can be enabled on an agent.
        const slackMissingLiveChatScopes = config.sysName === 'slack' &&
            Boolean(existingIntegration) &&
            !['channels:history', 'groups:history', 'chat:write.customize'].every(
                (scope) => (existingIntegration?.details?.scopes || []).includes(scope)
            );

        const connectButton = (
            <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={config.connectHandler}
                disabled={checkingLicense || (!hasAccess)}
            >
                {checkingLicense ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                    </>
                ) : !hasAccess ? (
                    requiredPlan === 'business' ? 'Upgrade to Convert' : 'Upgrade to Engage'
                ) : (
                    'Connect'
                )}
            </Button>
        );

        if (fetching) {
            return <SkeletonIntegrationCard key={config.sysName} />
        }

        return (
            <Card
                key={config.sysName}
                className="group flex min-h-[218px] flex-col overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:border-foreground/15 hover:shadow-md"
            >
                <CardHeader className="p-5 pb-3">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-center gap-2">
                            <CardTitle className="truncate text-[15px] font-semibold">
                                {config.name}
                            </CardTitle>
                            {isLegacyCalendly && (
                                <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                    Legacy
                                </span>
                            )}
                            {slackMissingLiveChatScopes && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <TriangleAlert className="h-4 w-4 flex-shrink-0 text-amber-500" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-[260px]">
                                            Reconnect Slack to grant the new permissions required for Live Chat Takeover (replying to visitors from Slack threads).
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </div>
                        <img
                            src={config.icon}
                            alt={`${config.name} logo`}
                            className="h-10 w-auto flex-shrink-0 object-contain"
                        />
                    </div>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-1">
                    <p className="text-[13px] leading-5 text-muted-foreground">
                        {config.description}
                    </p>
                </CardContent>
                <CardFooter className="mt-auto p-5 pt-0">
                    {isMultiLinkCalendar && matchingIntegrations.length > 0 ? (
                        <div className="flex w-full flex-col gap-2">
                            {matchingIntegrations.map((link) => {
                                const label = calendarLinkLabel(link.details) || 'Meeting link';
                                return (
                                    <div key={link.id} className="flex items-center gap-2">
                                        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" title={label}>
                                            {label}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => deleteIntegration(link.id)}
                                            className="h-8 shrink-0 px-2 text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                                            aria-label={`Disconnect ${label}`}
                                            title="Disconnect"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                );
                            })}
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                onClick={config.connectHandler}
                                disabled={checkingLicense || (!hasAccess)}
                            >
                                <Plus className="mr-1.5 h-3.5 w-3.5" />
                                Add another
                            </Button>
                        </div>
                    ) : existingIntegration ? (
                        <div className="@container flex w-full gap-2">
                            {['slack', 'google-ads', 'hubspot', 'salesforce', 'attio'].includes(config.sysName) ? (
                                <>
                                    <Button 
                                        variant="outline"
                                        size="sm"
                                        className="min-w-0 flex-1 px-2"
                                        onClick={config.connectHandler}
                                        aria-label={config.reconnectLabel || `Reconnect ${config.name}`}
                                        title={config.reconnectLabel || 'Reconnect'}
                                    >
                                        <RefreshCw className="h-3.5 w-3.5 shrink-0 @[240px]:mr-1.5" />
                                        <span className="hidden truncate @[240px]:inline">
                                            {config.reconnectLabel || 'Reconnect'}
                                        </span>
                                    </Button>
                                    <Button 
                                        variant="outline"
                                        size="sm"
                                        onClick={() => deleteIntegration(existingIntegration.id)}
                                        className="min-w-0 flex-1 px-2 text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                                        aria-label={`Disconnect ${config.name}`}
                                        title="Disconnect"
                                    >
                                        <Trash2 className="h-3.5 w-3.5 shrink-0 @[240px]:mr-1.5" />
                                        <span className="hidden truncate @[240px]:inline">
                                            Disconnect
                                        </span>
                                    </Button>
                                </>
                            ) : (
                                <Button 
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteIntegration(existingIntegration.id)}
                                    className="w-full text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                                >
                                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                                    Disconnect
                                </Button>
                            )}
                        </div>
                    ) : (
                        connectButton
                    )}
                </CardFooter>
                {config.sysName === 'chili-piper' && chiliPiperModalVisible && (
                    <ChiliPiper
                        createIntegration={createIntegration}
                        setModalVisible={setChiliPiperModalVisible}
                        modalVisible={chiliPiperModalVisible}
                        cpDomainName={existingIntegration?.details.domain_name}
                        cpInboundRouter={existingIntegration?.details.inbound_router}
                    />
                )}
                {config.sysName === 'default' && defaultModalVisible && (
                    <DefaultCom
                        createIntegration={createIntegration}
                        setModalVisible={setDefaultModalVisible}
                        modalVisible={defaultModalVisible}
                        existingFormId={existingIntegration?.details.form_id}
                        existingTeamId={existingIntegration?.details.team_id}
                    />
                )}
                {config.sysName === 'revenue-hero' && revenueHeroModalVisible && (
                    <RevenueHero
                        createIntegration={createIntegration}
                        setModalVisible={setRevenueHeroModalVisible}
                        modalVisible={revenueHeroModalVisible}
                        existingRouterId={existingIntegration?.details.router_id}
                    />
                )}
                {config.sysName === 'calendly' && calendlyModalVisible && (
                    <Calendly
                        integrationId={existingIntegration?.id}
                        setModalVisible={setCalendlyModalVisible}
                        modalVisible={calendlyModalVisible}
                        onComplete={() => {
                            if (getSafeReturnTo()) {
                                redirectAfterIntegrationSuccess();
                                return;
                            }
                            window.location.href = '/integrations';
                        }}
                    />
                )}
                {config.sysName === 'tidycal' && tidyCalModalVisible && (
                    <TidyCal
                        tidyCalBookingUrl=""
                        createIntegration={createIntegration}
                        setModalVisible={setTidyCalModalVisible}
                        modalVisible={tidyCalModalVisible}
                    />
                )}
                {config.sysName === 'cal-com' && calComModalVisible && (
                    <CalCom
                        calComBookingUrl=""
                        createIntegration={createIntegration}
                        setModalVisible={setCalComModalVisible}
                        modalVisible={calComModalVisible}
                    />
                )}
                {config.sysName === 'hubspot-meetings' && hubspotMeetingsModalVisible && (
                    <HubSpotMeetings
                        createIntegration={createIntegration}
                        setModalVisible={setHubspotMeetingsModalVisible}
                        modalVisible={hubspotMeetingsModalVisible}
                    />
                )}
            </Card>
        );
    };

    return (
        <div className="p-12 overflow-y-auto max-h-[calc(100vh-1px)] scrollbar-thin grow">
            <h1 className='text-4xl font-bold flex items-center gap-2'>Integrations</h1>
            <p className='mt-4 text-sm text-slate-500'>
                Connect your agents to other tools and platforms to automate your workflows.
            </p>
            <Separator className="my-4" />
            
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="mb-2">
                    <TabsTrigger value="integrations">Integrations</TabsTrigger>
                    <TabsTrigger value="api-keys">API Keys</TabsTrigger>
                    <TabsTrigger value="api-webhooks">Webhooks</TabsTrigger>
                    <TabsTrigger value="identity-verification">Identity Verification</TabsTrigger>
                    <TabsTrigger value="mcp-servers">MCP Servers</TabsTrigger>
                    {commerceEnabled && <TabsTrigger value="stripe-payments">Stripe Payments</TabsTrigger>}
                </TabsList>
                
                <TabsContent value="integrations" className="mt-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {integrationConfigs.map(config => renderIntegrationCard(config))}
                    </div>
                </TabsContent>
                
                <TabsContent value="api-keys" className="mt-6">
                    <APIKeys />
                </TabsContent>
                
                <TabsContent value="api-webhooks" className="mt-6">
                    <Webhooks />
                </TabsContent>

                <TabsContent value="identity-verification" className="mt-6">
                    <IdentityVerification />
                </TabsContent>

                <TabsContent value="mcp-servers" className="mt-6">
                    <McpServers />
                </TabsContent>

                <TabsContent value="stripe-payments" className="mt-6">
                    <StripePayments />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default Integrations;